import React, { type ReactNode, useMemo } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import { Modal } from "@/components";
import MapSettings from "@/models/map-settings";

import MapSettingsCreate from "./mode/create";
import MapSettingsEdit from "./mode/edit";

const Modes = {
    create: {
        component: MapSettingsCreate,
        title: "Lights Map Setup",
    },
    edit: {
        component: MapSettingsEdit,
        title: "Map Settings",
    },
};

interface SettingsModalProps {
    /**
     * The map settings configuration.
     */
    mapSettings: MapSettings;
    /**
     * Callback to toggle the visibility of the settings modal.
     */
    toggleVisibility: () => void;
    /**
     * Whether the settings modal is visible.
     */
    visible: boolean;
}

/**
 * Modal for creating or editing the lights map settings.
 *
 * @param {SettingsModalProps} props - The props for the SettingsModal component.
 * @returns {ReactNode} The rendered SettingsModal component.
 */
const SettingsModalComponent = ({
    mapSettings,
    toggleVisibility,
    visible,
}: SettingsModalProps): ReactNode => {
    const mode = useMemo(() => {
        const name = !mapSettings.hasMap ? "create" : "edit";
        return {
            ...Modes[name],
            name,
        };
    }, [mapSettings.hasMap]);

    const { title, component, name } = mode;
    const modeProps = {};
    const modalProps = {
        visible: visible,
        title: title,
        onClose: name === "create" ? null : toggleVisibility,
        slotProps: {
            paper: {
                sx: {
                    maxWidth: "40rem",
                    width: "40rem",
                    minHeight: "40rem",
                },
            },
        },
    };

    return (
        <Modal {...modalProps}>
            {React.createElement(component, modeProps)}
        </Modal>
    );
};

const mapStateToProps = ({ map, window }) => ({
    mapSettings: map.general.settings,
    visible:
        !window.loading.active &&
        (!map.general.settings.hasMap || map.general.settingsModalVisible),
});

const mapDispatchToProps = {
    toggleVisibility: actions.map.general.toggleSettingsVisibility,
};

export const SettingsModal = connect(
    mapStateToProps,
    mapDispatchToProps,
)(SettingsModalComponent);
